import { apiClient } from './apiClient'
import { environment } from '../../config/environment'
import type { ProductCategory, GalenicForm } from '../../types/category'
import { logger } from '../../utils/logger'

export class CategoriesService {
  /**
   * Get all categories with their subcategories and galenic forms
   */
  static async getCategories(): Promise<ProductCategory[]> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await CategoriesService.getMockCategories()
    }
    
    logger.log('📦 Fetching categories from API...')
    const response = await apiClient.get<any>('/v1/categories')
    logger.log('✅ Categories received:', response)
    
    return response.categories || []
  }

  /**
   * Create a new category
   */
  static async createCategory(category: Omit<ProductCategory, 'id'>): Promise<ProductCategory> {
    const createdCategory = await apiClient.post<ProductCategory>('/v1/categories', category)
    return createdCategory
  }

  /**
   * Update an existing category
   */
  static async updateCategory(category: ProductCategory): Promise<ProductCategory> {
    if (!category.id) {
      throw new Error('Category ID is required for update')
    }
    const updatedCategory = await apiClient.put<ProductCategory>(`/v1/categories/${category.id}`, category)
    return updatedCategory
  }

  /**
   * Delete a category
   */
  static async deleteCategory(id: number): Promise<void> {
    await apiClient.delete(`/v1/categories/${id}`)
  }

  /**
   * Get all galenic forms
   */
  static async getGalenicForms(): Promise<GalenicForm[]> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await CategoriesService.getMockGalenicForms()
    }
    
    logger.log('📦 Fetching galenic forms from API...')
    const response = await apiClient.get<any>('/v1/galenic-forms')
    logger.log('✅ Galenic forms received:', response)
    
    return response.galenicForms || []
  }

  /**
   * Create a new subcategory
   */
  static async createSubcategory(
    categoryId: number,
    name: string,
    galenicFormsIds: number[]
  ): Promise<void> {
    await apiClient.post(`/v1/categories/${categoryId}/subcategories`, {
      name,
      galenicForms: galenicFormsIds,
    })
  }

  /**
   * Update an existing subcategory
   */
  static async updateSubcategory(
    categoryId: number,
    subcategoryId: number,
    name: string,
    galenicFormsIds: number[]
  ): Promise<void> {
    await apiClient.put(`/v1/categories/${categoryId}/subcategories/${subcategoryId}`, {
      name,
      galenicForms: galenicFormsIds,
    })
  }

  /**
   * Delete a subcategory
   */
  static async deleteSubcategory(categoryId: number, subcategoryId: number): Promise<void> {
    await apiClient.delete(`/v1/categories/${categoryId}/subcategories/${subcategoryId}`)
  }

  /**
   * Mock data for development/testing
   */
  private static async getMockCategories(): Promise<ProductCategory[]> {
    const mockCategories: ProductCategory[] = [
      {
        id: 1,
        name: 'Soins du visage',
        subCategories: [
          {
            id: 1,
            name: 'Nettoyant',
            galenicForms: [
              { id: 1, name: 'gel', displayName: 'Gel' },
              { id: 2, name: 'mousse', displayName: 'Mousse' },
              { id: 3, name: 'lotion', displayName: 'Lotion' },
            ]
          },
          {
            id: 2,
            name: 'Hydratant',
            galenicForms: [
              { id: 4, name: 'crème', displayName: 'Crème' },
              { id: 5, name: 'sérum', displayName: 'Sérum' },
              { id: 6, name: 'huile', displayName: 'Huile' },
            ]
          },
          {
            id: 3,
            name: 'Protection solaire',
            galenicForms: [
              { id: 4, name: 'crème', displayName: 'Crème' },
              { id: 2, name: 'mousse', displayName: 'Mousse' },
              { id: 7, name: 'spray', displayName: 'Spray' },
            ]
          }
        ]
      },
      {
        id: 2,
        name: 'Soins du corps',
        subCategories: [
          {
            id: 4,
            name: 'Hydratant corps',
            galenicForms: [
              { id: 4, name: 'crème', displayName: 'Crème' },
              { id: 8, name: 'lait', displayName: 'Lait' },
              { id: 6, name: 'huile', displayName: 'Huile' },
            ]
          },
          {
            id: 5,
            name: 'Nettoyant corps',
            galenicForms: [
              { id: 1, name: 'gel', displayName: 'Gel' },
              { id: 9, name: 'savon', displayName: 'Savon' },
            ]
          }
        ]
      },
      {
        id: 3,
        name: 'Cheveux',
        subCategories: [
          {
            id: 6,
            name: 'Shampooing',
            galenicForms: [
              { id: 10, name: 'liquide', displayName: 'Liquide' },
              { id: 9, name: 'savon', displayName: 'Savon solide' },
            ]
          },
          {
            id: 7,
            name: 'Après-shampooing',
            galenicForms: [
              { id: 4, name: 'crème', displayName: 'Crème' },
              { id: 8, name: 'lait', displayName: 'Lait' },
            ]
          }
        ]
      }
    ]
    
    return new Promise((resolve) => {
      setTimeout(() => resolve(mockCategories), 300)
    })
  }

  private static async getMockGalenicForms(): Promise<GalenicForm[]> {
    const mockForms: GalenicForm[] = [
      { id: 1, name: 'gel', displayName: 'Gel' },
      { id: 2, name: 'mousse', displayName: 'Mousse' },
      { id: 3, name: 'lotion', displayName: 'Lotion' },
      { id: 4, name: 'crème', displayName: 'Crème' },
      { id: 5, name: 'sérum', displayName: 'Sérum' },
      { id: 6, name: 'huile', displayName: 'Huile' },
      { id: 7, name: 'spray', displayName: 'Spray' },
      { id: 8, name: 'lait', displayName: 'Lait' },
      { id: 9, name: 'savon', displayName: 'Savon' },
      { id: 10, name: 'liquide', displayName: 'Liquide' },
    ]
    
    return new Promise((resolve) => {
      setTimeout(() => resolve(mockForms), 300)
    })
  }
}